/**
 * CategoryDismissModal Component
 * Modal for dismissing all comments of a category.
 */
import React, { useState } from 'react';

interface CategoryDismissModalProps {
  category: string;
  submissionId: string;
  affectedCount: number;
  isOpen: boolean;
  onDismiss: (submissionId: string, category: string) => Promise<number>;
  onClose: () => void;
}

const CategoryDismissModal: React.FC<CategoryDismissModalProps> = ({
  category,
  submissionId,
  affectedCount,
  isOpen,
  onDismiss,
  onClose,
}) => {
  const [isDismissing, setIsDismissing] = useState(false);

  const handleDismiss = async () => {
    setIsDismissing(true);
    try {
      await onDismiss(submissionId, category);
      onClose();
    } catch {
      // Error is handled by the hook
    } finally {
      setIsDismissing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      handleDismiss();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 animate-backdrop-enter"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 animate-modal-enter"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Dismiss Category
          </h3>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-gray-700">
            This will dismiss{' '}
            <span className="font-semibold text-gray-900">
              {affectedCount} comment{affectedCount !== 1 ? 's' : ''}
            </span>{' '}
            in the &quot;{category}&quot; category for this submission.
          </p>

          <div className="mt-4 p-3 bg-yellow-50 rounded-md border border-yellow-200">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> This only affects the current submission.
              Future submissions will still receive feedback in this category.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={isDismissing}
          >
            Cancel
          </button>
          <button
            onClick={handleDismiss}
            disabled={isDismissing}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDismissing
              ? 'Dismissing...'
              : `Dismiss ${affectedCount} Comment${affectedCount !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategoryDismissModal;
